import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { BackButton } from '@/components/BackButton';
import SEO from '@/components/SEO';
import { PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';
import { 
  BookOpen, Search, Volume2, Car, Droplets, Shield, Route, GraduationCap, Wrench,
  Gauge, Battery, Fuel, Thermometer, Sun, SunDim, SunMedium, CloudFog,
  ArrowUp, ArrowDown, ArrowRight,
  CornerDownRight, CornerDownLeft, RotateCw, Undo2,
  TriangleAlert, Bell, Droplet, Flame, Plus, Construction, TrainFront,
  Eye, EyeOff, LogIn, LogOut, LayoutDashboard, CreditCard, IdCard,
  PersonStanding, UserCheck, User, Lightbulb, OctagonX, SquareParking,
  ScanEye, Shirt, Package, Circle, Disc, MoveDown, Hand, Signpost,
  ShieldCheck, Flashlight, CircleGauge
} from 'lucide-react';
import { API_URL } from '@/config';

// Custom SVG icons for car-specific items
const SteeringWheelIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 14v7" />
    <path d="M5.5 8.5l5.5 3.5" />
    <path d="M18.5 8.5l-5.5 3.5" />
  </svg>
);

const PedalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="3" width="8" height="18" rx="2" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
);

const GearStickIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="3" />
    <line x1="12" y1="8" x2="12" y2="21" />
    <path d="M8 18h8" />
  </svg>
);

const MirrorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="10" rx="8" ry="6" />
    <line x1="12" y1="16" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

const TrafficLightIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <circle cx="12" cy="7" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="17" r="2" />
  </svg>
);

const ExhaustIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16h6a2 2 0 0 0 2-2V6" />
    <path d="M16 8c1 0 2 .5 2 2s-1 2-2 2" />
    <path d="M19 6c1 0 2 .5 2 2s-1 2-2 2" />
    <circle cx="4" cy="16" r="2" />
  </svg>
);

const WiperIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h20" />
    <path d="M12 20V8" />
    <path d="M12 8L6 16" />
    <path d="M12 8l6 8" />
    <circle cx="12" cy="6" r="2" />
  </svg>
);

const SeatBeltIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 3l5 18" />
    <path d="M17 3l-5 18" />
    <rect x="5" y="9" width="14" height="4" rx="1" />
  </svg>
);

// Word-to-icon mapping
const wordIconMap = {
  // CONTROLS
  'Ratten': SteeringWheelIcon,
  'Gaspedalen': PedalIcon,
  'Bromspedalen': PedalIcon,
  'Kopplingspedalen': PedalIcon,
  'Växelspaken': GearStickIcon,
  'Handbromsen': Hand,
  'Blinkers': Flashlight,
  'Signalhornet': Bell,
  'Vindrutetorkarna': WiperIcon,
  'Halvljus': SunDim,
  'Helljus': Sun,
  'Dimljus': CloudFog,
  'Varningsblinkers': TriangleAlert,
  'Backljus': Lightbulb,
  // FLUIDS
  'Spolarvätska': Droplets,
  'Bromsvätska': Droplet,
  'Motoroljan': Droplet,
  'Kylarvätska': Thermometer,
  'Bränsle': Fuel,
  // SAFETY
  'Säkerhetsbältet': SeatBeltIcon,
  'Nackskyddet': ShieldCheck,
  'Krockkudden': Shield,
  'Varningstriangeln': TriangleAlert,
  'Brandsläckare': Flame,
  'Första hjälpen': Plus,
  'Reflexväst': Shirt,
  'Dödvinkel': EyeOff,
  // PARTS
  'Backspegeln': MirrorIcon,
  'Sidospegeln': MirrorIcon,
  'Motorhuven': Car,
  'Bagageutrymmet': Package,
  'Däcken': Circle,
  'Däcktryck': CircleGauge,
  'Batteriet': Battery,
  'Avgasröret': ExhaustIcon,
  'Katalysatorn': Wrench,
  'Hastighetsmätaren': Gauge,
  'Registreringsskylten': CreditCard,
  'Instrumentbrädan': LayoutDashboard,
  // ROAD & TRAFFIC
  'Övergångsstället': PersonStanding,
  'Cirkulationsplats': RotateCw,
  'Motorvägen': Route,
  'Körfält': Signpost,
  'Avfart': LogOut,
  'Påfart': LogIn,
  'Vägren': ArrowRight,
  'Trafiksignal': TrafficLightIcon,
  'Hastighetsbegränsning': CircleGauge,
  'Enkelriktad': ArrowRight,
  'Vägarbete': Construction,
  'Järnvägskorsning': TrainFront,
  // DRIVING TEST
  'Körkort': IdCard,
  'Uppkörning': Car,
  'Teoriprovet': BookOpen,
  'Övningsköra': CircleGauge,
  'Trafikskola': GraduationCap,
  'Handledare': User,
  'Inspektören': UserCheck,
  'Sväng till höger': CornerDownRight,
  'Sväng till vänster': CornerDownLeft,
  'Kör rakt fram': ArrowUp,
  'Stanna här': OctagonX,
  'Parkera': SquareParking,
  'Backa': ArrowDown,
  'Fickparkering': SquareParking,
  'Vänd': Undo2,
  'Sakta ner': MoveDown,
  'Lämna företräde': TriangleAlert,
  'Kontrollera speglarna': ScanEye,
};

// All translations for UI text
const ui = {
  pageTitle: {
    sv: 'Viktiga Ord', en: 'Important Words', ar: 'كلمات هامة', tr: 'Önemli Kelimeler',
    fa: 'کلمات مهم', so: 'Erayada Muhiimka ah', pl: 'Ważne Słowa', es: 'Palabras Importantes', de: 'Wichtige Wörter'
  },
  pageSubtitle: {
    sv: 'Viktiga termer och ord som du och inspektören använder under körprovet',
    en: 'Important terms and words used by you and the examiner during the driving test',
    ar: 'مصطلحات واسماء هامة سوف تستخدمها انت والفاحص اثناء فحصك العملي',
    tr: 'Sürücü sınavında siz ve müfettişin kullanacağı önemli terimler ve kelimeler',
    fa: 'اصطلاحات و کلمات مهمی که شما و ممتحن در آزمون رانندگی عملی استفاده می‌کنید',
    so: 'Ereyga iyo macnaha muhiimka ah ee adiga iyo baaritaanka isticmaalaysaan inta lagu jiro imtixaanka wadista',
    pl: 'Ważne terminy i słowa używane przez ciebie i egzaminatora podczas egzaminu na prawo jazdy',
    es: 'Términos y palabras importantes que usará usted y el examinador durante el examen de conducir',
    de: 'Wichtige Begriffe und Wörter die Sie und der Prüfer während der Fahrprüfung verwenden'
  },
  searchPlaceholder: {
    sv: 'Sök bland orden...', en: 'Search words...', ar: 'ابحث في الكلمات...', tr: 'Kelimelerde ara...',
    fa: 'جستجو در کلمات...', so: 'Raadi erayada...', pl: 'Szukaj słów...', es: 'Buscar palabras...', de: 'Wörter suchen...'
  },
  allCategories: {
    sv: 'Alla', en: 'All', ar: 'الكل', tr: 'Hepsi', fa: 'همه', so: 'Dhammaan', pl: 'Wszystkie', es: 'Todos', de: 'Alle'
  },
  wordCount: {
    sv: 'ord', en: 'words', ar: 'كلمة', tr: 'kelime', fa: 'کلمه', so: 'eray', pl: 'słów', es: 'palabras', de: 'Wörter'
  },
  listenTip: {
    sv: 'Tryck på ett ord för att höra uttalet', en: 'Click a word to hear the pronunciation',
    ar: 'اضغط على الكلمة لسماع النطق', tr: 'Telaffuzu duymak için bir kelimeye tıklayın',
    fa: 'برای شنیدن تلفظ روی کلمه کلیک کنید', so: 'Guji eray si aad u maqasho sida loo dhaho',
    pl: 'Kliknij słowo aby usłyszeć wymowę', es: 'Haga clic en una palabra para escuchar la pronunciación',
    de: 'Klicken Sie auf ein Wort um die Aussprache zu hören'
  }
};

// Category definitions
const categories = [
  { id: 'controls', icon: Car, color: 'emerald',
    label: { sv: 'Kontroller', en: 'Controls', ar: 'أدوات التحكم', tr: 'Kontroller', fa: 'کنترل‌ها', so: 'Xakamaynta', pl: 'Sterowanie', es: 'Controles', de: 'Bedienelemente' }
  },
  { id: 'fluids', icon: Droplets, color: 'blue',
    label: { sv: 'Vätskor', en: 'Fluids', ar: 'السوائل', tr: 'Sıvılar', fa: 'مایعات', so: 'Dareereyaasha', pl: 'Płyny', es: 'Líquidos', de: 'Flüssigkeiten' }
  },
  { id: 'safety', icon: Shield, color: 'amber',
    label: { sv: 'Säkerhet', en: 'Safety', ar: 'السلامة', tr: 'Güvenlik', fa: 'ایمنی', so: 'Badbaado', pl: 'Bezpieczeństwo', es: 'Seguridad', de: 'Sicherheit' }
  },
  { id: 'parts', icon: Wrench, color: 'violet',
    label: { sv: 'Bildelar', en: 'Car Parts', ar: 'أجزاء السيارة', tr: 'Araba Parçaları', fa: 'قطعات خودرو', so: 'Qaybaha Baabuurka', pl: 'Części samochodu', es: 'Partes del Auto', de: 'Autoteile' }
  },
  { id: 'road', icon: Route, color: 'rose',
    label: { sv: 'Trafik & Väg', en: 'Traffic & Road', ar: 'المرور والطرق', tr: 'Trafik & Yol', fa: 'ترافیک و جاده', so: 'Taraafikada & Jidka', pl: 'Ruch & Droga', es: 'Tráfico & Carretera', de: 'Verkehr & Straße' }
  },
  { id: 'test', icon: GraduationCap, color: 'cyan',
    label: { sv: 'Körprov', en: 'Driving Test', ar: 'فحص القيادة', tr: 'Sürücü Sınavı', fa: 'آزمون رانندگی', so: 'Imtixaanka Wadista', pl: 'Egzamin na prawo jazdy', es: 'Examen de Conducir', de: 'Fahrprüfung' }
  },
];

// Comprehensive vocabulary data: { swedish, category, translations per language }
const vocabularyData = [
  // CONTROLS
  { sv: 'Ratten', cat: 'controls', en: 'Steering wheel', ar: 'المقود / الدركسيون', tr: 'Direksiyon', fa: 'فرمان', so: 'Hagaaga', pl: 'Kierownica', es: 'Volante', de: 'Lenkrad' },
  { sv: 'Gaspedalen', cat: 'controls', en: 'Gas pedal', ar: 'دواسة البنزين', tr: 'Gaz pedalı', fa: 'پدال گاز', so: 'Biidhaamaha gaaska', pl: 'Pedał gazu', es: 'Pedal del acelerador', de: 'Gaspedal' },
  { sv: 'Bromspedalen', cat: 'controls', en: 'Brake pedal', ar: 'دواسة الفرامل', tr: 'Fren pedalı', fa: 'پدال ترمز', so: 'Biidhaamaha biriigga', pl: 'Pedał hamulca', es: 'Pedal del freno', de: 'Bremspedal' },
  { sv: 'Kopplingspedalen', cat: 'controls', en: 'Clutch pedal', ar: 'دواسة الكلتش / الدبرياج', tr: 'Debriyaj pedalı', fa: 'پدال کلاچ', so: 'Biidhaamaha kalajka', pl: 'Pedał sprzęgła', es: 'Pedal del embrague', de: 'Kupplungspedal' },
  { sv: 'Växelspaken', cat: 'controls', en: 'Gear stick', ar: 'عصا التعشيق / القير', tr: 'Vites kolu', fa: 'دسته دنده', so: 'Ulka geerka', pl: 'Dźwignia zmiany biegów', es: 'Palanca de cambios', de: 'Schalthebel' },
  { sv: 'Handbromsen', cat: 'controls', en: 'Handbrake', ar: 'فرامل اليد', tr: 'El freni', fa: 'ترمز دستی', so: 'Biriigga gacanka', pl: 'Hamulec ręczny', es: 'Freno de mano', de: 'Handbremse' },
  { sv: 'Blinkers', cat: 'controls', en: 'Turn signal', ar: 'إشارة الانعطاف / الغماز', tr: 'Sinyal lambası', fa: 'راهنما', so: 'Nalka leexashada', pl: 'Kierunkowskaz', es: 'Intermitente', de: 'Blinker' },
  { sv: 'Signalhornet', cat: 'controls', en: 'Horn', ar: 'البوق / الهورن', tr: 'Korna', fa: 'بوق', so: 'Buunka', pl: 'Klakson', es: 'Bocina', de: 'Hupe' },
  { sv: 'Vindrutetorkarna', cat: 'controls', en: 'Windshield wipers', ar: 'مساحات الزجاج', tr: 'Silecekler', fa: 'برف‌پاک‌کن', so: 'Tirtireyaasha daaqadda', pl: 'Wycieraczki', es: 'Limpiaparabrisas', de: 'Scheibenwischer' },
  { sv: 'Halvljus', cat: 'controls', en: 'Low beam headlights', ar: 'الإضاءة المنخفضة', tr: 'Kısa farlar', fa: 'نور پایین', so: 'Nalka hoose', pl: 'Światła mijania', es: 'Luces cortas', de: 'Abblendlicht' },
  { sv: 'Helljus', cat: 'controls', en: 'High beam headlights', ar: 'الإضاءة العالية', tr: 'Uzun farlar', fa: 'نور بالا', so: 'Nalka sare', pl: 'Światła drogowe', es: 'Luces largas', de: 'Fernlicht' },
  { sv: 'Dimljus', cat: 'controls', en: 'Fog lights', ar: 'مصابيح الضباب', tr: 'Sis farları', fa: 'چراغ مه‌شکن', so: 'Nalka ceeryaanka', pl: 'Światła przeciwmgielne', es: 'Luces antiniebla', de: 'Nebelscheinwerfer' },
  { sv: 'Varningsblinkers', cat: 'controls', en: 'Hazard warning lights', ar: 'إشارات التحذير / الفلاشر', tr: 'Dörtlü flaşör', fa: 'چراغ خطر', so: 'Nalka digniinta', pl: 'Światła awaryjne', es: 'Luces de emergencia', de: 'Warnblinker' },
  { sv: 'Backljus', cat: 'controls', en: 'Reverse lights', ar: 'مصابيح الرجوع للخلف', tr: 'Geri vites lambası', fa: 'چراغ دنده عقب', so: 'Nalka dib-u-socoshada', pl: 'Światła cofania', es: 'Luces de marcha atrás', de: 'Rückfahrscheinwerfer' },

  // FLUIDS
  { sv: 'Spolarvätska', cat: 'fluids', en: 'Windshield washer fluid', ar: 'سائل مساحات الزجاج', tr: 'Cam suyu', fa: 'مایع شیشه‌شوی', so: 'Biyaha daaqadda', pl: 'Płyn do spryskiwaczy', es: 'Líquido limpiaparabrisas', de: 'Scheibenwaschflüssigkeit' },
  { sv: 'Bromsvätska', cat: 'fluids', en: 'Brake fluid', ar: 'زيت الفرامل', tr: 'Fren sıvısı', fa: 'روغن ترمز', so: 'Saliidda biriigga', pl: 'Płyn hamulcowy', es: 'Líquido de frenos', de: 'Bremsflüssigkeit' },
  { sv: 'Motoroljan', cat: 'fluids', en: 'Engine oil', ar: 'زيت المحرك', tr: 'Motor yağı', fa: 'روغن موتور', so: 'Saliidda matoorka', pl: 'Olej silnikowy', es: 'Aceite de motor', de: 'Motoröl' },
  { sv: 'Kylarvätska', cat: 'fluids', en: 'Coolant', ar: 'سائل التبريد', tr: 'Antifriz', fa: 'مایع خنک‌کننده', so: 'Biyaha qaboojinta', pl: 'Płyn chłodzący', es: 'Líquido refrigerante', de: 'Kühlflüssigkeit' },
  { sv: 'Bränsle', cat: 'fluids', en: 'Fuel', ar: 'الوقود / البنزين', tr: 'Yakıt', fa: 'سوخت', so: 'Shidaalka', pl: 'Paliwo', es: 'Combustible', de: 'Kraftstoff' },

  // SAFETY
  { sv: 'Säkerhetsbältet', cat: 'safety', en: 'Seat belt', ar: 'حزام الأمان', tr: 'Emniyet kemeri', fa: 'کمربند ایمنی', so: 'Suunka badbaadada', pl: 'Pas bezpieczeństwa', es: 'Cinturón de seguridad', de: 'Sicherheitsgurt' },
  { sv: 'Nackskyddet', cat: 'safety', en: 'Headrest', ar: 'مسند الرأس', tr: 'Başlık', fa: 'پشت‌سری', so: 'Tiirka madaxa', pl: 'Zagłówek', es: 'Reposacabezas', de: 'Kopfstütze' },
  { sv: 'Krockkudden', cat: 'safety', en: 'Airbag', ar: 'الوسادة الهوائية', tr: 'Hava yastığı', fa: 'کیسه هوا', so: 'Boorsooyinka hawada', pl: 'Poduszka powietrzna', es: 'Bolsa de aire', de: 'Airbag' },
  { sv: 'Varningstriangeln', cat: 'safety', en: 'Warning triangle', ar: 'مثلث التحذير', tr: 'Uyarı üçgeni', fa: 'مثلث خطر', so: 'Saddex-xaglaha digniinta', pl: 'Trójkąt ostrzegawczy', es: 'Triángulo de advertencia', de: 'Warndreieck' },
  { sv: 'Brandsläckare', cat: 'safety', en: 'Fire extinguisher', ar: 'طفاية الحريق', tr: 'Yangın söndürücü', fa: 'کپسول آتش‌نشانی', so: 'Damiyaha dabka', pl: 'Gaśnica', es: 'Extintor', de: 'Feuerlöscher' },
  { sv: 'Första hjälpen', cat: 'safety', en: 'First aid kit', ar: 'حقيبة الإسعافات الأولية', tr: 'İlk yardım çantası', fa: 'جعبه کمک‌های اولیه', so: 'Sanduuqa gargaarka degdegga', pl: 'Apteczka', es: 'Botiquín de primeros auxilios', de: 'Erste-Hilfe-Kasten' },
  { sv: 'Reflexväst', cat: 'safety', en: 'Reflective vest', ar: 'سترة عاكسة', tr: 'Reflektif yelek', fa: 'جلیقه بازتابنده', so: 'Jaakadda iftiinka', pl: 'Kamizelka odblaskowa', es: 'Chaleco reflectante', de: 'Warnweste' },
  { sv: 'Dödvinkel', cat: 'safety', en: 'Blind spot', ar: 'النقطة العمياء', tr: 'Kör nokta', fa: 'نقطه کور', so: 'Meesha aan la arki karin', pl: 'Martwy punkt', es: 'Punto ciego', de: 'Toter Winkel' },

  // PARTS
  { sv: 'Backspegeln', cat: 'parts', en: 'Rearview mirror', ar: 'المرآة الخلفية', tr: 'Dikiz aynası', fa: 'آینه عقب', so: 'Muraayada gadaasha', pl: 'Lusterko wsteczne', es: 'Espejo retrovisor', de: 'Rückspiegel' },
  { sv: 'Sidospegeln', cat: 'parts', en: 'Side mirror', ar: 'المرآة الجانبية', tr: 'Yan ayna', fa: 'آینه بغل', so: 'Muraayada dhinacyada', pl: 'Lusterko boczne', es: 'Espejo lateral', de: 'Seitenspiegel' },
  { sv: 'Motorhuven', cat: 'parts', en: 'Hood / Bonnet', ar: 'غطاء المحرك / الكبوت', tr: 'Motor kaputu', fa: 'کاپوت', so: 'Daboolka matoorka', pl: 'Maska silnika', es: 'Capó', de: 'Motorhaube' },
  { sv: 'Bagageutrymmet', cat: 'parts', en: 'Trunk / Boot', ar: 'صندوق السيارة / الشنطة', tr: 'Bagaj', fa: 'صندوق عقب', so: 'Sanduuqa dambe', pl: 'Bagażnik', es: 'Maletero', de: 'Kofferraum' },
  { sv: 'Däcken', cat: 'parts', en: 'Tires', ar: 'الإطارات / العجلات', tr: 'Lastikler', fa: 'لاستیک‌ها', so: 'Taayirada', pl: 'Opony', es: 'Neumáticos', de: 'Reifen' },
  { sv: 'Däcktryck', cat: 'parts', en: 'Tire pressure', ar: 'ضغط الإطارات', tr: 'Lastik basıncı', fa: 'فشار باد لاستیک', so: 'Cadaadiska taayirka', pl: 'Ciśnienie w oponach', es: 'Presión de neumáticos', de: 'Reifendruck' },
  { sv: 'Batteriet', cat: 'parts', en: 'Battery', ar: 'البطارية', tr: 'Akü', fa: 'باتری', so: 'Baytariga', pl: 'Akumulator', es: 'Batería', de: 'Batterie' },
  { sv: 'Avgasröret', cat: 'parts', en: 'Exhaust pipe', ar: 'أنبوب العادم / الشكمان', tr: 'Egzoz borusu', fa: 'لوله اگزوز', so: 'Tuubbada qiiqa', pl: 'Rura wydechowa', es: 'Tubo de escape', de: 'Auspuffrohr' },
  { sv: 'Katalysatorn', cat: 'parts', en: 'Catalytic converter', ar: 'المحول الحفاز', tr: 'Katalitik konvertör', fa: 'کاتالیزور', so: 'Katalaysatarka', pl: 'Katalizator', es: 'Catalizador', de: 'Katalysator' },
  { sv: 'Hastighetsmätaren', cat: 'parts', en: 'Speedometer', ar: 'عداد السرعة', tr: 'Hız göstergesi', fa: 'سرعت‌سنج', so: 'Cabbiraaha xawaaraha', pl: 'Prędkościomierz', es: 'Velocímetro', de: 'Tacho / Geschwindigkeitsmesser' },
  { sv: 'Registreringsskylten', cat: 'parts', en: 'License plate', ar: 'لوحة تسجيل السيارة', tr: 'Plaka', fa: 'پلاک', so: 'Lanbarka gaadiidka', pl: 'Tablica rejestracyjna', es: 'Placa de matrícula', de: 'Nummernschild' },
  { sv: 'Instrumentbrädan', cat: 'parts', en: 'Dashboard', ar: 'لوحة القيادة / الطبلون', tr: 'Gösterge paneli', fa: 'داشبورد', so: 'Saxanka aaladaha', pl: 'Deska rozdzielcza', es: 'Tablero de instrumentos', de: 'Armaturenbrett' },

  // ROAD & TRAFFIC
  { sv: 'Övergångsstället', cat: 'road', en: 'Pedestrian crossing', ar: 'ممر المشاة', tr: 'Yaya geçidi', fa: 'گذرگاه عابر پیاده', so: 'Meesha lugeeyaha', pl: 'Przejście dla pieszych', es: 'Paso de peatones', de: 'Fußgängerüberweg' },
  { sv: 'Cirkulationsplats', cat: 'road', en: 'Roundabout', ar: 'الدوار', tr: 'Dönel kavşak', fa: 'میدان', so: 'Wareega taraafikada', pl: 'Rondo', es: 'Rotonda', de: 'Kreisverkehr' },
  { sv: 'Motorvägen', cat: 'road', en: 'Highway / Motorway', ar: 'الطريق السريع', tr: 'Otoban', fa: 'بزرگراه', so: 'Waddada weyn', pl: 'Autostrada', es: 'Autopista', de: 'Autobahn' },
  { sv: 'Körfält', cat: 'road', en: 'Lane', ar: 'المسار / الحارة', tr: 'Şerit', fa: 'خط عبور', so: 'Dariiqa wadista', pl: 'Pas ruchu', es: 'Carril', de: 'Fahrspur' },
  { sv: 'Avfart', cat: 'road', en: 'Exit', ar: 'مخرج', tr: 'Çıkış', fa: 'خروجی', so: 'Bixitaanka', pl: 'Zjazd', es: 'Salida', de: 'Ausfahrt' },
  { sv: 'Påfart', cat: 'road', en: 'On-ramp', ar: 'مدخل الطريق السريع', tr: 'Giriş', fa: 'ورودی', so: 'Gelitaanka', pl: 'Wjazd', es: 'Entrada', de: 'Auffahrt' },
  { sv: 'Vägren', cat: 'road', en: 'Road shoulder', ar: 'جانب الطريق', tr: 'Banket', fa: 'شانه جاده', so: 'Garbaha jidka', pl: 'Pobocze', es: 'Arcén', de: 'Seitenstreifen' },
  { sv: 'Trafiksignal', cat: 'road', en: 'Traffic signal', ar: 'إشارة المرور', tr: 'Trafik lambası', fa: 'چراغ راهنمایی', so: 'Nalka taraafikada', pl: 'Sygnalizacja świetlna', es: 'Semáforo', de: 'Ampel' },
  { sv: 'Hastighetsbegränsning', cat: 'road', en: 'Speed limit', ar: 'حد السرعة', tr: 'Hız sınırı', fa: 'محدودیت سرعت', so: 'Xadka xawaaraha', pl: 'Ograniczenie prędkości', es: 'Límite de velocidad', de: 'Geschwindigkeitsbegrenzung' },
  { sv: 'Enkelriktad', cat: 'road', en: 'One-way street', ar: 'شارع باتجاه واحد', tr: 'Tek yönlü yol', fa: 'خیابان یک‌طرفه', so: 'Jid hal-geesle ah', pl: 'Ulica jednokierunkowa', es: 'Calle de sentido único', de: 'Einbahnstraße' },
  { sv: 'Vägarbete', cat: 'road', en: 'Road work', ar: 'أشغال على الطريق', tr: 'Yol çalışması', fa: 'کار جاده‌ای', so: 'Dhismaha jidka', pl: 'Roboty drogowe', es: 'Obras en la carretera', de: 'Straßenarbeiten' },
  { sv: 'Järnvägskorsning', cat: 'road', en: 'Railway crossing', ar: 'تقاطع سكة حديد', tr: 'Demir yolu geçidi', fa: 'گذرگاه ریلی', so: 'Goobta isgoyska tareenka', pl: 'Przejazd kolejowy', es: 'Paso a nivel', de: 'Bahnübergang' },

  // DRIVING TEST
  { sv: 'Körkort', cat: 'test', en: 'Driving license', ar: 'رخصة القيادة', tr: 'Sürücü belgesi', fa: 'گواهینامه رانندگی', so: 'Shatiga wadista', pl: 'Prawo jazdy', es: 'Licencia de conducir', de: 'Führerschein' },
  { sv: 'Uppkörning', cat: 'test', en: 'Practical driving test', ar: 'فحص القيادة العملي', tr: 'Pratik sürücü sınavı', fa: 'آزمون عملی رانندگی', so: 'Imtixaanka wadista ficiliga', pl: 'Egzamin praktyczny', es: 'Examen práctico', de: 'Praktische Fahrprüfung' },
  { sv: 'Teoriprovet', cat: 'test', en: 'Theory test', ar: 'فحص النظري / التيوري', tr: 'Teori sınavı', fa: 'آزمون تئوری', so: 'Imtixaanka aragtiida', pl: 'Egzamin teoretyczny', es: 'Examen teórico', de: 'Theorieprüfung' },
  { sv: 'Övningsköra', cat: 'test', en: 'Practice driving', ar: 'تدريب القيادة', tr: 'Sürücü eğitimi', fa: 'تمرین رانندگی', so: 'Tababarka wadista', pl: 'Jazda ćwiczebna', es: 'Conducción de práctica', de: 'Übungsfahrt' },
  { sv: 'Trafikskola', cat: 'test', en: 'Driving school', ar: 'مدرسة القيادة', tr: 'Sürücü kursu', fa: 'آموزشگاه رانندگی', so: 'Iskuulka wadista', pl: 'Szkoła jazdy', es: 'Autoescuela', de: 'Fahrschule' },
  { sv: 'Handledare', cat: 'test', en: 'Instructor / Supervisor', ar: 'المرافق / المدرب', tr: 'Eğitmen', fa: 'مربی', so: 'Macalinka', pl: 'Instruktor', es: 'Instructor', de: 'Fahrlehrer' },
  { sv: 'Inspektören', cat: 'test', en: 'The examiner', ar: 'الفاحص / الممتحن', tr: 'Müfettiş', fa: 'ممتحن', so: 'Baaritaanka', pl: 'Egzaminator', es: 'Examinador', de: 'Prüfer' },
  { sv: 'Sväng till höger', cat: 'test', en: 'Turn right', ar: 'انعطف يميناً', tr: 'Sağa dön', fa: 'به راست بپیچید', so: 'U leexo midig', pl: 'Skręć w prawo', es: 'Gire a la derecha', de: 'Rechts abbiegen' },
  { sv: 'Sväng till vänster', cat: 'test', en: 'Turn left', ar: 'انعطف يساراً', tr: 'Sola dön', fa: 'به چپ بپیچید', so: 'U leexo bidix', pl: 'Skręć w lewo', es: 'Gire a la izquierda', de: 'Links abbiegen' },
  { sv: 'Kör rakt fram', cat: 'test', en: 'Drive straight ahead', ar: 'سر للأمام مباشرة', tr: 'Düz gidin', fa: 'مستقیم بروید', so: 'Toos u wad', pl: 'Jedź prosto', es: 'Siga recto', de: 'Geradeaus fahren' },
  { sv: 'Stanna här', cat: 'test', en: 'Stop here', ar: 'قف هنا', tr: 'Burada dur', fa: 'اینجا توقف کنید', so: 'Halkan joogso', pl: 'Zatrzymaj się tutaj', es: 'Deténgase aquí', de: 'Halten Sie hier an' },
  { sv: 'Parkera', cat: 'test', en: 'Park', ar: 'اركن هنا', tr: 'Park et', fa: 'پارک کنید', so: 'Gaadiidka dhig', pl: 'Zaparkuj', es: 'Estacione', de: 'Parken' },
  { sv: 'Backa', cat: 'test', en: 'Reverse', ar: 'ارجع للخلف', tr: 'Geri git', fa: 'عقب بروید', so: 'Dib u soco', pl: 'Cofaj', es: 'Marcha atrás', de: 'Rückwärts fahren' },
  { sv: 'Fickparkering', cat: 'test', en: 'Parallel parking', ar: 'الركن الموازي', tr: 'Paralel park', fa: 'پارک موازی', so: 'Baarkinta dhinaca jidka', pl: 'Parkowanie równoległe', es: 'Estacionamiento en paralelo', de: 'Einparken' },
  { sv: 'Vänd', cat: 'test', en: 'U-turn', ar: 'استدر / لف', tr: 'U dönüşü yap', fa: 'دور بزنید', so: 'U wareeg', pl: 'Zawróć', es: 'De la vuelta', de: 'Wenden' },
  { sv: 'Sakta ner', cat: 'test', en: 'Slow down', ar: 'خفف السرعة', tr: 'Yavaşla', fa: 'آهسته‌تر بروید', so: 'Yaree', pl: 'Zwolnij', es: 'Reduzca la velocidad', de: 'Langsamer fahren' },
  { sv: 'Lämna företräde', cat: 'test', en: 'Yield / Give way', ar: 'أعط الأولوية', tr: 'Yol ver', fa: 'حق تقدم بدهید', so: 'Mudnaanta sii', pl: 'Ustąp pierwszeństwa', es: 'Ceda el paso', de: 'Vorfahrt gewähren' },
  { sv: 'Kontrollera speglarna', cat: 'test', en: 'Check the mirrors', ar: 'تحقق من المرايا', tr: 'Aynaları kontrol et', fa: 'آینه‌ها را چک کنید', so: 'Hubi muraayooyinka', pl: 'Sprawdź lusterka', es: 'Revise los espejos', de: 'Spiegel prüfen' },
];

// TTS function using Edge-TTS through backend
const speakSwedish = async (text) => {
  try {
    const response = await fetch(`${API_URL}/api/voice/speak`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, language: 'sv' })
    });
    if (response.ok) {
      const blob = await response.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.play();
    } else {
      throw new Error('TTS failed');
    }
  } catch (e) {
    // Fallback to browser TTS
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'sv-SE';
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  }
};

const getCategoryColor = (colorName) => {
  const colors = {
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-400', icon: 'text-emerald-600 dark:text-emerald-400', activeBg: 'bg-emerald-500', pill: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' },
    blue: { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-400', icon: 'text-blue-600 dark:text-blue-400', activeBg: 'bg-blue-500', pill: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-400', icon: 'text-amber-600 dark:text-amber-400', activeBg: 'bg-amber-500', pill: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' },
    violet: { bg: 'bg-violet-50 dark:bg-violet-950/30', border: 'border-violet-200 dark:border-violet-800', text: 'text-violet-700 dark:text-violet-400', icon: 'text-violet-600 dark:text-violet-400', activeBg: 'bg-violet-500', pill: 'bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300' },
    rose: { bg: 'bg-rose-50 dark:bg-rose-950/30', border: 'border-rose-200 dark:border-rose-800', text: 'text-rose-700 dark:text-rose-400', icon: 'text-rose-600 dark:text-rose-400', activeBg: 'bg-rose-500', pill: 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300' },
    cyan: { bg: 'bg-cyan-50 dark:bg-cyan-950/30', border: 'border-cyan-200 dark:border-cyan-800', text: 'text-cyan-700 dark:text-cyan-400', icon: 'text-cyan-600 dark:text-cyan-400', activeBg: 'bg-cyan-500', pill: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300' },
  };
  return colors[colorName] || colors.emerald;
};

export default function ImportantWordsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'sv';
  const isRTL = lang === 'ar' || lang === 'fa';
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [playingWord, setPlayingWord] = useState(null);

  const t = (key) => ui[key]?.[lang] || ui[key]?.en || key;

  const filteredWords = vocabularyData.filter(word => {
    const matchesCategory = selectedCategory === 'all' || word.cat === selectedCategory;
    if (!searchQuery.trim()) return matchesCategory;
    const q = searchQuery.toLowerCase();
    const translation = word[lang] || word.en || '';
    return matchesCategory && (
      word.sv.toLowerCase().includes(q) ||
      translation.toLowerCase().includes(q) ||
      word.en.toLowerCase().includes(q)
    );
  });

  const handleSpeak = async (text) => {
    setPlayingWord(text);
    await speakSwedish(text);
    setTimeout(() => setPlayingWord(null), 2000);
  };

  const getCatObj = (catId) => categories.find(c => c.id === catId);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#09090b]" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO page="important-words" />
      <PageBreadcrumbs page="important-words" language={i18n.language} />
      <Navbar />
      <VisibleBreadcrumbs />

      <main className="pt-3 pb-16" data-testid="important-words-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">

          {/* Back Button */}
          <div className="mb-4">
            <BackButton />
          </div>

          {/* Hero Section */}
          <div className="relative mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 sm:p-8" data-testid="words-hero">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-4 right-8 w-24 h-24 rounded-full bg-emerald-400 blur-2xl" />
              <div className="absolute bottom-4 left-8 w-32 h-32 rounded-full bg-blue-400 blur-2xl" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-emerald-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="words-title">
                  {t('pageTitle')}
                </h1>
              </div>
              <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
                {t('pageSubtitle')}
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <Volume2 className="w-4 h-4" />
                <span>{t('listenTip')}</span>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6" data-testid="words-search">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 ${isRTL ? 'right-4' : 'left-4'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 rounded-xl border border-slate-200 dark:border-[#27272a] bg-white dark:bg-[#18181b] text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all text-sm`}
              data-testid="words-search-input"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }} data-testid="words-category-filter">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md'
                  : 'bg-white dark:bg-[#18181b] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#27272a] hover:border-slate-300'
              }`}
              data-testid="filter-all"
            >
              <span className="whitespace-nowrap">{t('allCategories')} ({vocabularyData.length})</span>
            </button>
            {categories.map(cat => {
              const count = vocabularyData.filter(w => w.cat === cat.id).length;
              const Icon = cat.icon;
              const colors = getCategoryColor(cat.color);
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                    selectedCategory === cat.id
                      ? `${colors.activeBg} text-white shadow-md`
                      : `bg-white dark:bg-[#18181b] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#27272a] hover:border-slate-300`
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">{cat.label[lang] || cat.label.en} ({count})</span>
                </button>
              );
            })}
          </div>

          {/* Word Count */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4" data-testid="words-count">
            {filteredWords.length} {t('wordCount')}
          </p>

          {/* Words List - grouped by category if "all" is selected */}
          {selectedCategory === 'all' ? (
            categories.map(cat => {
              const catWords = filteredWords.filter(w => w.cat === cat.id);
              if (catWords.length === 0) return null;
              const colors = getCategoryColor(cat.color);
              const Icon = cat.icon;
              return (
                <div key={cat.id} className="mb-8" data-testid={`category-section-${cat.id}`}>
                  <div className={`flex items-center gap-2 mb-3 px-1`}>
                    <div className={`w-8 h-8 rounded-lg ${colors.pill} flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <h2 className={`text-base font-semibold ${colors.text}`}>
                      {cat.label[lang] || cat.label.en}
                    </h2>
                    <span className="text-xs text-slate-400">({catWords.length})</span>
                  </div>
                  <div className="space-y-2">
                    {catWords.map((word, idx) => (
                      <WordCard key={idx} word={word} lang={lang} isRTL={isRTL} onSpeak={handleSpeak} playingWord={playingWord} catColor={cat.color} />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="space-y-2">
              {filteredWords.map((word, idx) => {
                const cat = getCatObj(word.cat);
                return (
                  <WordCard key={idx} word={word} lang={lang} isRTL={isRTL} onSpeak={handleSpeak} playingWord={playingWord} catColor={cat?.color || 'emerald'} />
                );
              })}
            </div>
          )}

          {filteredWords.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg">{lang === 'ar' ? 'لا توجد نتائج' : lang === 'sv' ? 'Inga resultat hittades' : 'No results found'}</p>
            </div>
          )}
        </div>
      </main>

      <div className="max-w-4xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="words" /></div>
      <Footer />
    </div>
  );
}

function WordCard({ word, lang, isRTL, onSpeak, playingWord, catColor }) {
  // عندما تكون اللغة سويدية، نعرض الترجمة الإنجليزية بدلاً من تكرار نفس الكلمة
  const translation = lang === 'sv' ? word.en : (word[lang] || word.en);
  const isPlaying = playingWord === word.sv;
  const colors = getCategoryColor(catColor);
  const IconComponent = wordIconMap[word.sv] || null;

  return (
    <div
      className={`group flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-[#18181b] border border-slate-100 dark:border-[#27272a] hover:border-slate-200 dark:hover:border-[#3f3f46] transition-all hover:shadow-sm`}
      data-testid={`word-card-${word.sv.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Word Icon */}
      {IconComponent && (
        <div className={`w-10 h-10 rounded-lg ${colors.pill} flex items-center justify-center flex-shrink-0`}>
          <IconComponent className="w-5 h-5" />
        </div>
      )}
      <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onSpeak(word.sv)}
            className={`inline-flex items-center gap-2 font-semibold text-lg text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors ${isPlaying ? 'text-emerald-600 dark:text-emerald-400' : ''}`}
            title="Listen"
            data-testid={`speak-${word.sv.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Volume2 className={`w-4 h-4 flex-shrink-0 ${isPlaying ? 'animate-pulse text-emerald-500' : 'text-slate-400 group-hover:text-emerald-500'} transition-colors`} />
            <span>{word.sv}</span>
          </button>
        </div>
        <p className={`mt-1 text-base ${colors.text} font-medium`}>
          {translation}
        </p>
        {lang !== 'en' && lang !== 'sv' && (
          <p className="mt-0.5 text-xs text-slate-400">
            {word.en}
          </p>
        )}
      </div>
    </div>
  );
}
